
/**
 * Module dependencies.
 */

import { Button } from '../buttons/button';
import { FlatButton } from '../buttons/flat-button';
import { FocusEvent, ReactNode } from 'react';
import { Modal, ModalProps } from './modal';
import { useTranslate } from '../../i18n';
import styled from 'styled-components';

/**
 * `ConfirmationModalProps` type.
 */

export type ConfirmationModalProps = ModalProps & {
  cancelButtonLabel?: string;
  children?: ReactNode;
  closeOnPressBackdrop?: boolean;
  confirmButtonLabel?: string;
  description?: ReactNode;
  desktopIcon?: string;
  disabled?: boolean;
  iconColor?: string | (() => void);
  isLoading?: boolean;
  mobileIcon?: string;
  onCancel: () => void;
  onConfirm: () => void;
  onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
  title?: ReactNode;
};

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  margin-bottom: 16px;
`;

/**
 * Export `ConfirmationModal` component.
 */

export function ConfirmationModal(props: ConfirmationModalProps): JSX.Element {
  const { cancelButtonLabel, children, confirmButtonLabel, disabled, isLoading, onCancel, onConfirm, ...rest } = props;
  const { translate } = useTranslate();

  return (
    <Modal {...rest}>
      {children}

      <StyledButton
        disabled={disabled}
        fullWidth
        isLoading={isLoading}
        onClick={onConfirm}
        size={'large'}
        type={'button'}
      >
        {confirmButtonLabel || translate('common.actions.confirm')}
      </StyledButton>

      <FlatButton
        fullWidth
        onClick={onCancel}
        size={'large'}
      >
        {cancelButtonLabel || translate('common.actions.cancel')}
      </FlatButton>
    </Modal>
  );
}
